import React, { useState } from "react";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import JR_Logo from '../assets/logo.png';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [activeNestedDropdown, setActiveNestedDropdown] = useState(null);
    const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
    const [mobileNestedOpen, setMobileNestedOpen] = useState(null);

    const menuItems = [
        { name: "Home", href: "/" },
        { name: "About Us", href: "/about" },
        {
            name: "Products",
            href: "/products",
            dropdown: [
                { 
                    name: "Refractory Bricks", 
                    path: "/products/refractory_bricks",
                    subMenu: [
                        { name: "Fireclay Bricks", path: "/products/fireclay_bricks" },
                        { name: "High Alumina Bricks", path: "/products/high_alumina_bricks" },
                        { name: "Cupola Bricks", path: "/products/cupola_bricks" },
                        { name: "Acid Resistant Bricks", path: "/products/acid_resistance_bricks" },
                        { name: "Magnesia Bricks", path: "/products/magnesia_refractory_bricks" },
                        { name: "Sillimanite Bricks", path: "/products/sillimanite_refractory_bricks" },
                    ]
                },
                { 
                    name: "Castable Monolithics", 
                    path: "/products/castable-monolithics",
                    subMenu: [
                        { name: "Castable Super", path: "/products/castable_super" },
                        { name: "White Heat-A", path: "/products/white_heat-a" },
                        { name: "White Heat-C", path: "/products/white_heat-c" },
                        { name: "White Heat-K", path: "/products/white_heat-k" },
                        { name: "Low Cement Castable-60", path: "/products/lcc-60" },
                        { name: "Low Cement Castable-90", path: "/products/lcc-90" },
                        { name: "Insulyte-7", path: "/products/insulyte-7" },
                        { name: "Insulyte-11", path: "/products/insulyte-11" },
                        { name: "Ramming Mass", path: "/products/ramming_mass" },
                    ]
                },
                { 
                    name: "Mortar", 
                    path: "/products/mortar",
                    subMenu: [
                        { name: "Fireclay", path: "/products/fireclay" },
                        { name: "IS-8 Morter", path: "/products/is-8-morter" },
                        { name: "ACCO SET-50", path: "/products/acco-set-50" },
                        { name: "70% Morter", path: "/products/morter-70" },
                        { name: "ACID Proof Morter", path: "/products/acid-proof-morter" },
                        { name: "Thermotex", path: "/products/thermotex" },
                    ]
                },
                { 
                    name: "Insulation Fiber Blanket", 
                    path: "/products/insulation-fiber-blanket",
                    subMenu: [
                        { name: "HFK Bricks", path: "/products/hfk-insulation-bricks" },
                        { name: "CFK Bricks", path: "/products/cfk-insulation-bricks" },
                        { name: "Ceramic Fiber Blanket", path: "/products/ceramic-fiber-blankets" },
                        { name: "Hysil Boards", path: "/products/calcium-silicate-boards" },
                        { name: "Ceramic Fiber Boards", path: "/products/ceramic-fiber-boards" },
                        { name: "Ceramic Fiber Modules", path: "/products/ceramic-fiber-modules" },
                    ]
                },
                { name: "Bauxite", path: "/products/bauxite" },
                { name: "Refractory Cement", path: "/products/refractory-cement" },
                { name: "Bed Materials", path: "/products/bed-materials" },
                { name: "Pre Cast Pre Fire Blocks", path: "/products/precast-prefire" },
            ],
        },
        { name: "Quality & Services", href: "/quality-services" },
        { name: "Infrastructure", href: "/infrastructure" },
        { name: "Contact", href: "/contact" },
    ];

    const handleMouseEnter = (itemName) => {
        setActiveDropdown(itemName);
    };

    const handleMouseLeave = () => {
        setActiveDropdown(null);
        setActiveNestedDropdown(null);
    };

    const handleNestedMouseEnter = (subItemName) => {
        setActiveNestedDropdown(subItemName);
    };

    const toggleMobileDropdown = (itemName, e) => {
        e.preventDefault();
        e.stopPropagation();
        setMobileDropdownOpen(mobileDropdownOpen === itemName ? null : itemName);
        setMobileNestedOpen(null);
    };

    const toggleMobileNested = (subItemName, e) => {
        e.preventDefault();
        e.stopPropagation();
        setMobileNestedOpen(mobileNestedOpen === subItemName ? null : subItemName);
    };

    return (
        <nav className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 shadow-lg sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <div className="flex items-center space-x-3">
                        <div className="w-40 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                            <Link to="/">
                                <img
                                    src={JR_Logo}
                                    alt="Janta Refractory Logo"
                                    className="w-40 h-16 object-contain mt-3"
                                />
                            </Link>
                        </div>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden lg:flex items-center space-x-1">
                        {menuItems.map((item) => {
                            const isActive = window.location.pathname === item.href;

                            return (
                                <div
                                    key={item.name}
                                    className="relative group"
                                    onMouseEnter={() => handleMouseEnter(item.name)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    {/* Main link */}
                                    <a
                                        href={item.href}
                                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                            isActive
                                                ? "text-orange-500 border-b-2 border-orange-500"
                                                : "text-gray-300 hover:text-white hover:bg-gray-700"
                                        }`}
                                    >
                                        {item.name}
                                        {item.dropdown && <ChevronDown className="ml-1 w-4 h-4" />}
                                    </a>

                                    {/* First Level Dropdown */}
                                    {item.dropdown && activeDropdown === item.name && (
                                        <div className="absolute left-0 pt-2" style={{ top: "100%" }}>
                                            <div className="w-64 bg-white rounded-xl shadow-2xl py-3 border border-gray-100">
                                                {item.dropdown.map((subItem, index) => (
                                                    <div
                                                        key={subItem.name}
                                                        className="relative"
                                                        onMouseEnter={() => handleNestedMouseEnter(subItem.name)}
                                                    >
                                                        <a
                                                            href={subItem.path}
                                                            className={`flex items-center justify-between px-5 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 transition-all duration-200 group/item ${
                                                                index !== 0 ? 'border-t border-gray-100' : ''
                                                            }`}
                                                        >
                                                            <span className="font-medium text-sm">{subItem.name}</span>
                                                            {subItem.subMenu && (
                                                                <ChevronRight className="w-4 h-4 group-hover/item:translate-x-1 transition-transform duration-200" />
                                                            )}
                                                        </a>

                                                        {/* Second Level Dropdown (Nested) */}
                                                        {subItem.subMenu && activeNestedDropdown === subItem.name && (
                                                            <div 
                                                                className="absolute left-full top-0 pl-2"
                                                                style={{ minWidth: '240px' }}
                                                            >
                                                                <div className="bg-white rounded-xl shadow-2xl py-3 border border-gray-100 max-h-96 overflow-y-auto">
                                                                    {subItem.subMenu.map((nestedItem, nestedIndex) => (
                                                                        <a
                                                                            key={nestedItem.name}
                                                                            href={nestedItem.path}
                                                                            className={`block px-5 py-2.5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 transition-all duration-200 hover:pl-6 ${
                                                                                nestedIndex !== 0 ? 'border-t border-gray-50' : ''
                                                                            }`}
                                                                        >
                                                                            {nestedItem.name}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* CTA */}
                        <button className="ml-4 px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                            Get Quote
                        </button>
                    </div>

                    {/* Mobile toggle */}
                    <div className="lg:hidden">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors duration-300"
                        >
                            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isOpen && (
                <div className="lg:hidden bg-gray-800 border-t border-gray-700 animate-slideDown">
                    <div className="px-4 py-4 space-y-2 max-h-[80vh] overflow-y-auto">
                        {menuItems.map((item) => {
                            const isActive = window.location.pathname === item.href;
                            const hasDropdown = item.dropdown && item.dropdown.length > 0;
                            
                            return (
                                <div key={item.name}>
                                    <div className="flex items-center">
                                        {/* Name - Navigate */}
                                        <a
                                            href={item.href}
                                            className={`flex-1 block px-4 py-3 rounded-l-lg transition-colors duration-300 font-medium ${
                                                isActive
                                                    ? "text-orange-500 bg-gray-700"
                                                    : "text-gray-300 hover:text-white hover:bg-gray-700"
                                            }`}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {item.name}
                                        </a>
                                        {/* Arrow - Toggle Dropdown */}
                                        {hasDropdown && (
                                            <button
                                                onClick={(e) => toggleMobileDropdown(item.name, e)}
                                                className={`px-4 py-3 rounded-r-lg transition-colors ${
                                                    isActive ? "bg-gray-700" : "hover:bg-gray-700"
                                                } text-gray-300 hover:text-white border-l border-gray-600`}
                                            >
                                                <ChevronDown 
                                                    className={`w-5 h-5 transition-transform duration-300 ${
                                                        mobileDropdownOpen === item.name ? 'rotate-180' : ''
                                                    }`}
                                                />
                                            </button>
                                        )}
                                    </div>

                                    {/* Dropdown Content */}
                                    {item.dropdown && mobileDropdownOpen === item.name && (
                                        <div className="ml-4 mt-2 space-y-2 animate-slideDown">
                                            {item.dropdown.map((subItem) => (
                                                <div key={subItem.name} className="border-l-2 border-gray-700 pl-4">
                                                    <div className="flex items-center">
                                                        {/* SubItem Name - Navigate */}
                                                        <a
                                                            href={subItem.path}
                                                            className="flex-1 block px-4 py-2 text-sm text-gray-400 hover:text-orange-500 hover:bg-gray-700 rounded-l-lg transition-colors duration-300"
                                                            onClick={() => setIsOpen(false)}
                                                        >
                                                            {subItem.name}
                                                        </a>
                                                        {/* Arrow - Toggle Nested */}
                                                        {subItem.subMenu && (
                                                            <button
                                                                onClick={(e) => toggleMobileNested(subItem.name, e)}
                                                                className="px-3 py-2 text-gray-400 hover:text-orange-500 hover:bg-gray-700 rounded-r-lg transition-colors border-l border-gray-600"
                                                            >
                                                                <ChevronRight 
                                                                    className={`w-4 h-4 transition-transform duration-300 ${
                                                                        mobileNestedOpen === subItem.name ? 'rotate-90' : ''
                                                                    }`}
                                                                />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {/* Nested Dropdown */}
                                                    {subItem.subMenu && mobileNestedOpen === subItem.name && (
                                                        <div className="ml-4 mt-1 space-y-1 animate-slideDown">
                                                            {subItem.subMenu.map((nestedItem) => (
                                                                <a
                                                                    key={nestedItem.name}
                                                                    href={nestedItem.path}
                                                                    className="block px-4 py-2 text-xs text-gray-500 hover:text-orange-400 hover:bg-gray-700 rounded-lg transition-colors duration-300"
                                                                    onClick={() => setIsOpen(false)}
                                                                >
                                                                    • {nestedItem.name}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <button className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-700 transition-all duration-300 shadow-lg">
                            Get Quote
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slideDown {
                    animation: slideDown 0.3s ease-out;
                }
            `}</style>
        </nav>
    );
};

export default Navbar;